import { useTranslation } from "next-i18next";
import { useCallback, useEffect, useMemo, useState } from "react";
import { useRouter } from "next/router";

import { useSubscription } from "@/hooks/useSubscription";
import { paymentApi } from "@/redux/api/paymentApi";
import { useNotification } from "@/hooks/useNotification";

import { IconPurchaseArrowUpCircle } from "../Icons";

import { PurchasePopup } from "./PurchasePopup";

export const ApiSubscriptionPurchasePopup = ({
  isOpened = false,
  onClose,
  plan,
}: // period,
{
  isOpened?: boolean;
  onClose?: () => void;
  plan?: "standard" | "premium";
  period?: "monthly" | "yearly";
}) => {
  const { t } = useTranslation("common");
  const router = useRouter();

  const { contextHolder, notify } = useNotification();

  /**
   * 마운트 여부 스테이트
   *
   * 팝업이 열리고 잠시 후에 마운트 여부를 true로 취급
   */
  const [isMounted, setIsMounted] = useState(false);

  useEffect(() => {
    if (isOpened) {
      setIsMounted(true);
    } else {
      setIsMounted(false);
    }

    return () => {
      setIsMounted(false);
    };
  }, [isOpened]);

  const { data: plans } = paymentApi.useGetPlansQuery({});

  const {
    apiPlan: currentPlan,
    refetch: refetchSubscription,
    apiSubscriptionInfo,
  } = useSubscription();

  /**
   * 현재 구독 상태 등 정리
   */
  const currentPlanFullName = currentPlan?.raw;
  const currentBillingCycle = useMemo(() => {
    if (!currentPlan?.servicePeriodStart || !currentPlan?.servicePeriodEnd) {
      return;
    }

    return {
      start: currentPlan.servicePeriodStart,
      end: currentPlan.servicePeriodEnd,
    };
  }, [currentPlan]);
  const nextBillingDate = useMemo(() => {
    if (!currentPlan?.servicePeriodEnd) {
      return;
    }

    return currentPlan.servicePeriodEnd;
  }, [currentPlan]);

  const subscriptionId = apiSubscriptionInfo?.subscriptionId;

  /**
   * 선택한 플랜
   */
  const foundPlan = useMemo(() => {
    if (!plans || !plan) {
      return;
    }

    return plans.find((p) => {
      const lowerCasedPlanName = p.planName.toLowerCase();
      return (
        lowerCasedPlanName.includes("api") && lowerCasedPlanName.includes(plan)
      );
    });
  }, [plans, plan]);
  const foundPlanName = foundPlan?.planName;
  const foundPlanPricePerMonth = foundPlan?.pricePerMonth;
  const foundPlanPriceId = foundPlan?.planId.monthly;

  const editingProducts = useMemo(() => {
    return [
      {
        productTitle: `${foundPlanName}`,
        productPrice: `$${foundPlanPricePerMonth}`,
        isMonthly: true,
        isBilledAnnually: false,
        min: 1,
        descriptions: [
          <>
            <IconPurchaseArrowUpCircle />
            {t(
              "Includes increased limits, priority performance, and full API access.",
            )}
          </>,
        ],
        isDisabledQuantity: true,
        quantity: 1,
        isSelected: true,
      },
    ];
  }, [foundPlanName, foundPlanPricePerMonth, t]);

  /**
   * 오늘 결제 금액 계산 관련
   */
  const {
    data: invoicePreviewData,
    isFetching: isInvoicePreviewFetching,
    isError: isInvoicePreviewError,
  } = paymentApi.useGetInvoicePreviewQuery(
    {
      type: "base",
      subscriptionId: subscriptionId ?? "",
      items: [
        {
          price: foundPlanPriceId ?? "",
          quantity: 1,
        },
      ],
    },
    {
      skip: !isMounted || !subscriptionId || !foundPlanPriceId,
    },
  );

  const totalPrice = useMemo(() => {
    const items = invoicePreviewData?.items;

    if (!items || !Array.isArray(items)) {
      return 0;
    }

    return items
      .filter((item) => !item.proration)
      .reduce((acc, item) => {
        return acc + (item.unit_amount ?? 0) * (item.quantity ?? 0);
      }, 0);
  }, [invoicePreviewData]);

  const todayBillingTotalPrice = invoicePreviewData?.due_today ?? 0;

  let discountInfo = null;
  const discount = apiSubscriptionInfo?.discount;
  if (discount) {
    if (discount.interval === "yearly") {
      discountInfo = {
        title: "10% off",
      };
    } else {
      discountInfo = {
        title: "50% off",
      };
    }
  }

  const { details, prorationDetails } = useMemo(() => {
    if (!invoicePreviewData) {
      return {};
    }

    const items = invoicePreviewData.items ?? [];

    const mergingDetails = [];
    const mergingProrationDetails = [];

    const getDescription = (item: (typeof items)[number]) => {
      const pName = item.planName;
      const pInterval = item.price.recurring.interval;
      const pQuantity = item.quantity;

      if (!item.price.lookup_key) return "";
      if (item.price.lookup_key.includes("base")) {
        return `(${pInterval === "month" ? "Monthly" : "Annual"})${
          pName.toLowerCase().includes("team")
            ? ` x ${pQuantity} ${t("Seat")}`
            : ""
        }`;
      } else if (item.price.lookup_key.includes("avatar")) {
        return `(${
          pInterval === "month" ? "Monthly" : "Annual"
        }) x ${pQuantity} ${t("Qty")}`;
      } else if (item.price.lookup_key.includes("genCredit")) {
        const credit = Number(
          item.description?.split(" - ")?.[1]?.substring(0, 3) ?? "0",
        );
        const month = pInterval === "month" ? 1 : 12;
        return `(${credit * month} credits) x ${pQuantity} ${t("Unit")}`;
      }
    };

    const getPriority = (item: (typeof items)[number]) => {
      if (!item.price.lookup_key) {
        return 999999;
      }

      if (item.price.lookup_key.includes("base")) {
        return 0;
      }

      if (item.price.lookup_key.includes("avatar")) {
        return 11;
      }

      if (item.price.lookup_key.includes("genCredit")) {
        return 12;
      }

      return 999999;
    };

    const sortedItems = [...items].sort((a, b) => {
      const aPriority = getPriority(a);
      const bPriority = getPriority(b);

      return aPriority - bPriority;
    });

    for (const item of sortedItems) {
      if (item.amount > 0) {
        if (!item.proration) {
          continue;
        }

        mergingDetails.push({
          title: item.planName,
          description: getDescription(item),
          price: item.amount,
        });
      } else {
        mergingProrationDetails.push({
          title: item.planName,
          description: getDescription(item),
          price: item.amount,
        });
      }
    }

    return {
      details: mergingDetails,
      prorationDetails: mergingProrationDetails,
    };
  }, [invoicePreviewData, t]);

  const nextBillingTotalPrice = totalPrice;

  /**
   * 결제 등 진행
   */
  const [upgradeSubscription, { isLoading: isLoadingUpgradeSubscription }] =
    paymentApi.useUpgradeSubscriptionMutation();

  const handleConfirm = useCallback(async () => {
    if (isLoadingUpgradeSubscription) {
      return;
    }

    if (!subscriptionId || !foundPlanPriceId) {
      return;
    }

    /**
     * 비로그인, 엔터프라이즈, 무료 플랜은 구매 불가
     *
     * 애초에 이 팝업이 표시되지 않음
     */
    if (currentPlan?.level.current === 0 || currentPlan?.level.current === 3) {
      return;
    }

    try {
      await upgradeSubscription({
        subscriptionId,
        items: [
          {
            price: foundPlanPriceId ?? "",
            quantity: 1,
          },
        ],
        type: "api",
      }).unwrap();

      await refetchSubscription();

      onClose?.();

      notify.success(
        t("Your plan has been updated and the changes are now in effect."),
      );

      router.reload();
    } catch (e) {
      // TODO: 에러 종류에 따라 다른 메시지 추가 필요
      if (false) {
        // 사용자 결제 정보 오류로 인한 결제 실패인 경우
        notify.warning(
          t(
            "Plan update failed. Please check your billing info and try again.",
          ),
        );
      } else {
        // 그 외 일반적인 오류
        notify.warning(
          t(
            "Plan update failed. Please try again. If the issue persists, contact support.",
          ),
        );
      }
    }
  }, [
    subscriptionId,
    foundPlanPriceId,
    currentPlan,
    upgradeSubscription,
    onClose,
    refetchSubscription,
    notify,
    t,
    isLoadingUpgradeSubscription,
    router,
  ]);

  /**
   * 결제 포탈 이동
   */

  const [successUrl, setSuccessUrl] = useState("");

  useEffect(() => {
    setSuccessUrl(window.location.href);
  }, [router]);

  const { data: billingPortalUrl } = paymentApi.useGetBillingPortalUrlQuery(
    {
      successUrl,
    },
    {
      skip: !isMounted || !successUrl,
    },
  );

  const goToBillingPortal = useCallback(async () => {
    if (!billingPortalUrl) {
      return;
    }

    window.open(billingPortalUrl, "_blank");
  }, [billingPortalUrl]);

  return (
    <>
      <PurchasePopup
        isOpened={isOpened}
        onClose={() => {
          if (isLoadingUpgradeSubscription) {
            return;
          }

          onClose?.();
        }}
        title={t("Update Subscription")}
        productsTitle={t("AI Studios API Plan")}
        productsSubTitle={t(
          "Upgrade your plan to unlock more features and flexibility",
        )}
        products={editingProducts}
        productsDescriptions={[
          t("Your subscription will be updated immediately."),
          t(
            "API limits and features will adjust automatically to your new plan.",
          ),
        ]}
        currentPlan={currentPlanFullName}
        currentBillingCycle={currentBillingCycle}
        totalPrice={totalPrice}
        details={details}
        prorationDetails={prorationDetails}
        todayPrice={todayBillingTotalPrice}
        loadingTodayPrice={isInvoicePreviewFetching}
        nextBillingPrice={nextBillingTotalPrice}
        nextBillingDate={nextBillingDate}
        confirmText={t("Confirm")}
        disabledConfirm={isInvoicePreviewError}
        loadingConfirm={isLoadingUpgradeSubscription}
        onConfirm={handleConfirm}
        goToBillingPortal={goToBillingPortal}
        discount={discountInfo}
      />

      {contextHolder}
    </>
  );
};
